import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { trueOwnersApi } from '../../requests/trueOwners';
import { formatClock, formatNumber, formatRange, formatRunTime } from '../ListingSources/format';

// A capture in progress moves; nothing else on the screen does.
const POLL_MS = 5000;

const STATE_LABEL = {
    idle: 'Not started',
    running: 'Running',
    paused: 'Paused',
    budget_exhausted: 'Waiting for budget',
    completed: 'Completed',
    failed: 'Failed',
    stopped: 'Stopped',
};

// Which of the design's pill treatments each state borrows: blue and pulsing
// only while something is actually happening, grey for every kind of waiting.
const STATE_PILL = {
    idle: 'ls-pill--schedule',
    running: 'ls-pill--running',
    paused: 'ls-pill--mode',
    budget_exhausted: 'ls-pill--schedule',
    completed: 'ls-pill--completed',
    failed: 'ls-pill--failed',
    stopped: 'ls-pill--mode',
};

const PAUSE_REASON = {
    manual: 'Paused by hand. Resume carries on from the same parcels.',
    quota: "Stopped itself: LightBox answered 429 twice, so this key's request pool is spent. Save a new key in Settings and it carries on from the same parcels.",
    key: 'Stopped itself: LightBox rejected the key. Save a working key in Settings and it carries on from the same parcels.',
};

// The four things a pass does to a parcel, in the order the cards read.
const COUNTS = [
    ['created', 'New people'],
    ['updated', 'Seen again'],
    ['skipped', 'No record'],
    ['failed', 'Failed'],
];

const STOPPED_PREFIX = 'Stopped by hand';

/** A pass ended by the Stop button is recorded as failed; it reads as stopped. */
function passState(run) {
    if (!run) return 'idle';
    if (run.status === 'failed' && run.error && run.error.indexOf(STOPPED_PREFIX) === 0) return 'stopped';
    return run.status;
}

function Pill({ state, cell }) {
    const base = cell ? 'ls-cellpill' : 'ls-pill';
    const variant = (STATE_PILL[state] || 'ls-pill--schedule').replace('ls-pill', base);
    return (
        <span className={base + ' ' + variant}>
            {(state === 'running' || state === 'completed' || state === 'failed') && <span className={base + '__dot'} />}
            {STATE_LABEL[state] || state}
        </span>
    );
}

function Counts({ run }) {
    return (
        <div className="ls-counts ls-counts--large">
            {COUNTS.map(([key, label]) => {
                const value = run[key] || 0;
                return (
                    <div key={key} className={'ls-count ls-count--' + key + (value ? '' : ' is-zero')}>
                        <div className="ls-count__value">{formatNumber(value)}</div>
                        <div className="ls-count__label">{label}</div>
                    </div>
                );
            })}
        </div>
    );
}

/** The design's cell pill for an item's outcome, in this screen's words. */
function OutcomeCell({ outcome }) {
    const label = (COUNTS.find(([key]) => key === outcome) || [outcome, outcome])[1];
    return <span className={'ls-cellpill ls-cellpill--' + (outcome === 'failed' ? 'failed-outcome' : outcome)}>{label}</span>;
}

/**
 * What one pass did, parcel by parcel, with every person it named linked to
 * the investor it became — the place to check what the capture put into the
 * system without a database.
 */
function PassDetail({ run, onBack }) {
    const firstWithSomething = COUNTS.map(([key]) => key).find((key) => run[key]) || 'created';
    const [outcome, setOutcome] = useState(firstWithSomething);
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback((wanted, chosen) => {
        setLoading(true);
        trueOwnersApi.runItems(run.id, chosen, wanted).then(
            (envelope) => {
                setItems(envelope.data || []);
                setMeta(envelope.meta ? envelope.meta.pagination : null);
                setPage(wanted);
                setLoading(false);
            },
            (failure) => {
                setError(failure.message || 'Could not load this pass.');
                setLoading(false);
            }
        );
    }, [run.id]);

    useEffect(() => {
        load(1, outcome);
    }, [load, outcome]);

    const lastPage = meta ? meta.last_page : 1;

    return (
        <div className="ls-detail">
            <button type="button" className="ls-backlink" onClick={onBack}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path d="M8.4 2.8 L4.4 6.5 L8.4 10.2" />
                </svg>
                All passes
            </button>

            <div className="ls-detail__head">
                <h1 className="ls-detail__title">{formatRunTime(run.started_at)}</h1>
                <Pill state={passState(run)} />
                <span className="ls-detail__time">
                    pass #{run.id}
                    {run.finished_at ? ' · ended ' + formatClock(run.finished_at) : ''}
                    {run.started_by ? ' · ' + run.started_by : ''}
                </span>
            </div>

            <Counts run={run} />

            <div className="ls-filters">
                {COUNTS.map(([key, label]) => (
                    <button key={key} type="button" className={'ls-filter' + (outcome === key ? ' is-active' : '')} onClick={() => setOutcome(key)}>
                        {label}
                        <span className={'ls-filter__count' + (run[key] ? '' : ' is-zero')}>{formatNumber(run[key])}</span>
                    </button>
                ))}
            </div>

            <div className="ls-card">
                {error && <div className="ls-empty">Could not load this pass. {error}</div>}
                {!error && loading && items.length === 0 && <div className="ls-empty">Loading parcels…</div>}
                {!error && !loading && items.length === 0 && <div className="ls-empty">No parcels with this outcome in this pass.</div>}

                {items.length > 0 && (
                    <div>
                        <div className="ls-table__scroll">
                            <div className="ls-table ls-table--people">
                                <div className="ls-table__head">
                                    <div>Parcel</div>
                                    <div>Outcome</div>
                                    <div>People</div>
                                    <div>Note</div>
                                </div>

                                {items.map((item) => (
                                    <div key={item.id} className="ls-table__row is-static">
                                        <div className="ls-mono ls-item__id">{item.external_id}</div>
                                        <div><OutcomeCell outcome={item.outcome} /></div>
                                        <div className="TrueOwners-people">
                                            {(item.notes || []).filter((note) => note.value).map((note) => (
                                                <Link key={note.value} className="ls-item__property" to={'/investor/' + note.value}>
                                                    {note.text}
                                                </Link>
                                            ))}
                                            {!(item.notes || []).some((note) => note.value) && <span className="ls-item__noproperty">—</span>}
                                        </div>
                                        <div className="ls-item__reason">{item.reason}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ls-pager">
                            <span className="ls-pager__label">{formatRange(meta, items.length)}</span>
                            <button type="button" className="ls-btn ls-btn--small" disabled={page <= 1} onClick={() => load(page - 1, outcome)}>
                                Previous
                            </button>
                            <button type="button" className="ls-btn ls-btn--small" disabled={page >= lastPage} onClick={() => load(page + 1, outcome)}>
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Note() {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="6" cy="6" r="4.4" />
            <path d="M6 3.6 V6.2 L7.8 7.4" />
        </svg>
    );
}

/**
 * The key and the pace, in the design's dialog.
 *
 * The key is what the capture stops for: when LightBox says the pool is
 * spent, saving the next key here is what gets it going again.
 */
function SettingsDialog({ settings, contactsKey, onSave, onClose }) {
    const [draft, setDraft] = useState({
        contacts_api_key: '',
        daily_budget: String(settings.daily_budget),
        request_delay_ms: String(settings.request_delay_ms),
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failure, setFailure] = useState(null);

    useEffect(() => {
        const onKeyDown = (event) => event.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const dirty = draft.contacts_api_key.trim() !== ''
        || draft.daily_budget !== String(settings.daily_budget)
        || draft.request_delay_ms !== String(settings.request_delay_ms);

    const set = (field, value) => {
        setDraft({ ...draft, [field]: value });
        setSaved(false);
    };

    const discard = () => {
        setDraft({
            contacts_api_key: '',
            daily_budget: String(settings.daily_budget),
            request_delay_ms: String(settings.request_delay_ms),
        });
        setFailure(null);
    };

    const save = () => {
        setSaving(true);
        setFailure(null);
        onSave({
            contacts_api_key: draft.contacts_api_key.trim(),
            daily_budget: Number(draft.daily_budget),
            request_delay_ms: Number(draft.request_delay_ms),
        }).then(
            () => {
                setSaving(false);
                setSaved(true);
                setDraft((current) => ({ ...current, contacts_api_key: '' }));
            },
            (error) => {
                setSaving(false);
                setFailure(error);
            }
        );
    };

    const invalid = (field) => failure && failure.errors && failure.errors[field];
    const budget = Number(draft.daily_budget);
    const delay = Number(draft.request_delay_ms);
    const keyHint = contactsKey.hint
        ? 'Current key ends in ' + contactsKey.hint.replace('…', '') + (contactsKey.source === 'env' ? ' (from the server environment)' : '')
        : 'No key is set';

    return (
        <div className="ls-modal">
            <div className="ls-modal__scrim" onClick={onClose} />

            <div className="ls-modal__dialog TrueOwners-dialog" role="dialog" aria-modal="true" aria-label="True owner capture settings">
                <div className="ls-modal__head">
                    <span className="ls-modal__title">Capture settings</span>
                    <button type="button" className="ls-modal__close" aria-label="Close" onClick={onClose}>×</button>
                </div>

                <div className="ls-modal__body">
                    {failure && (
                        <div className="ls-modal__alert">
                            <span className="ls-alert__mark">!</span>
                            {failure.message}
                        </div>
                    )}

                    <div className="ls-modal__columns">
                        <div className="ls-group">
                            <div className="ls-group__head">
                                <span className="ls-group__title">LightBox key</span>
                                <span className="ls-group__meta">{keyHint}</span>
                            </div>
                            <label className="ls-field ls-field--spaced">
                                <span className="ls-field__label">New contacts key</span>
                                <input
                                    type="password"
                                    autoComplete="off"
                                    className={'ls-input ls-mono' + (invalid('contacts_api_key') ? ' is-invalid' : '')}
                                    placeholder="Leave blank to keep the current key"
                                    value={draft.contacts_api_key}
                                    onChange={(event) => set('contacts_api_key', event.target.value)}
                                />
                            </label>
                            <div className="ls-note"><Note /> A stopped pass carries on by itself once a key is saved</div>
                        </div>

                        <div className="ls-group">
                            <div className="ls-group__head">
                                <span className="ls-group__title">Daily cap</span>
                            </div>
                            <label className="ls-field ls-field--spaced">
                                <span className="ls-field__label">Requests a day</span>
                                <div className="ls-inline">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={'ls-input ls-input--narrow ls-mono' + (invalid('daily_budget') ? ' is-invalid' : '')}
                                        value={draft.daily_budget}
                                        onChange={(event) => set('daily_budget', event.target.value)}
                                    />
                                    <span className="ls-inline__hint">
                                        {budget > 0 ? 'stops for the day at ' + formatNumber(budget) : '0 = no cap; the key\'s own pool is the limit'}
                                    </span>
                                </div>
                            </label>
                        </div>

                        <div className="ls-group">
                            <div className="ls-group__head">
                                <span className="ls-group__title">Pace</span>
                            </div>
                            <label className="ls-field ls-field--spaced">
                                <span className="ls-field__label">Milliseconds between requests</span>
                                <div className="ls-inline">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={'ls-input ls-input--narrow ls-mono' + (invalid('request_delay_ms') ? ' is-invalid' : '')}
                                        value={draft.request_delay_ms}
                                        onChange={(event) => set('request_delay_ms', event.target.value)}
                                    />
                                    <span className="ls-inline__hint">
                                        {delay > 0 ? '≈ ' + formatNumber(Math.floor(60000 / delay)) + ' requests a minute per lane' : 'as fast as LightBox answers'}
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <div className={'ls-modal__foot' + (dirty ? ' is-dirty' : '')}>
                    <span className="ls-modal__hint">{saved && !dirty ? 'Saved' : dirty ? 'Unsaved changes' : ''}</span>
                    <button type="button" className="ls-discard" onClick={discard}>Discard</button>
                    <button type="button" className="ls-save" disabled={!dirty || saving} onClick={save}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * The true owners screen.
 *
 * Styled with the listing sources design — same shell, same pills, same
 * cards — because it answers the same three questions: is it on, how far has
 * it got, what did it do. Nothing to configure but the key and the pace: the
 * capture always wants every owner in every ZIP the client works.
 */
export default function TrueOwners() {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [openRun, setOpenRun] = useState(null);
    const [runs, setRuns] = useState([]);
    const [runsMeta, setRunsMeta] = useState(null);
    const [page, setPage] = useState(1);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const unmounted = useRef(false);

    const load = useCallback(() => {
        return trueOwnersApi.status().then(
            (data) => {
                if (unmounted.current) return;
                setStatus(data);
                setRefreshedAt(Date.now());
            },
            (failure) => {
                if (unmounted.current) return;
                setError(failure.message || 'The true owners screen could not be loaded.');
            }
        );
    }, []);

    const loadRuns = useCallback((wanted) => {
        return trueOwnersApi.runs(wanted).then((envelope) => {
            if (unmounted.current) return;
            setRuns(envelope.data || []);
            setRunsMeta(envelope.meta ? envelope.meta.pagination : null);
            setPage(wanted);
        }, () => {});
    }, []);

    useEffect(() => {
        unmounted.current = false;
        load();
        loadRuns(1);
        return () => {
            unmounted.current = true;
        };
    }, [load, loadRuns]);

    // Re-ask only while something is actually moving.
    useEffect(() => {
        if (!status || status.state !== 'running') return undefined;
        const timer = setTimeout(() => {
            load();
            loadRuns(page);
        }, POLL_MS);
        return () => clearTimeout(timer);
    }, [status, load, loadRuns, page]);

    const act = (name, request, wording) => {
        setBusy(name);
        setNotice(null);
        request().then(
            (data) => {
                if (unmounted.current) return;
                setBusy(null);
                const refused = data.started === false || data.resumed === false || data.stopped === false;
                setNotice(refused ? { kind: 'warn', text: data.reason } : { kind: 'ok', text: wording });
                load();
                loadRuns(1);
            },
            (failure) => {
                if (unmounted.current) return;
                setBusy(null);
                setNotice({ kind: 'error', text: failure.message || 'The request failed.' });
            }
        );
    };

    // The whole status is re-read, not just the settings: a new key or a
    // raised cap changes the state and the bands as well.
    const saveSettings = (settings) => trueOwnersApi.saveSettings(settings).then((data) => {
        load();
        return data;
    });

    const closeSettings = useCallback(() => setSettingsOpen(false), []);

    if (error) {
        return (
            <div className="ListingSources TrueOwners">
                <div className="ls-blockedwrap">
                    <div className="ls-blocked">
                        <div className="ls-blocked__mark" />
                        <div className="ls-blocked__title">Nothing to show</div>
                        <p className="ls-blocked__text">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!status) {
        return (
            <div className="ListingSources TrueOwners">
                <div className="ls-container"><div className="ls-empty">Loading true owners…</div></div>
            </div>
        );
    }

    const { state, run, progress, usage, settings } = status;
    const running = state === 'running';
    const active = Boolean(run) && run.status === 'running';
    const shownState = active ? state : passState(run);
    const percent = progress.zips_total
        ? Math.min(100, Math.round((progress.zips_done / progress.zips_total) * 100))
        : 0;
    const age = refreshedAt ? Math.max(0, Math.round((Date.now() - refreshedAt) / 1000)) : 0;
    const notes = (run && run.notes) || [];
    const lastNote = notes.length ? notes[notes.length - 1] : null;
    const stopped = run && passState(run) === 'stopped';
    const walking = (progress.lanes || []).filter((lane) => lane.zip && !lane.done);
    const laneCount = (progress.lanes || []).length;
    const stoppedForKey = active && !settings.enabled && (status.paused_reason === 'quota' || status.paused_reason === 'key');

    if (openRun) {
        // The current pass keeps moving while it is open, so its counts come
        // from the freshest status rather than the row that was clicked.
        const shown = run && run.id === openRun.id ? run : openRun;
        return (
            <div className="ListingSources TrueOwners">
                <div className="ls-container">
                    <PassDetail run={shown} onBack={() => { setOpenRun(null); loadRuns(1); }} />
                </div>
            </div>
        );
    }

    return (
        <div className="ListingSources TrueOwners">
            <div className="ls-container">
                <div className="ls-header">
                    <div className="ls-header__headings">
                        <h1 className="ls-header__title">True Owners</h1>
                        <div className="ls-header__meta">
                            <Pill state={shownState} />
                            <span className="ls-header__schedule">
                                Every ZIP the client works · {formatNumber(progress.zips_total)} ZIPs
                                · {laneCount} lane{laneCount === 1 ? '' : 's'}
                                · {settings.daily_budget > 0 ? formatNumber(settings.daily_budget) + ' requests a day' : 'no daily cap'}
                                · {settings.request_delay_ms > 0 ? formatNumber(settings.request_delay_ms) + ' ms between requests' : 'no wait between requests'}
                                · key {status.contacts_key.hint || 'not set'}
                            </span>
                        </div>
                    </div>

                    <div className="ls-header__actions">
                        <button type="button" className="ls-btn" onClick={() => setSettingsOpen(true)}>
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor"
                                strokeWidth="1.4" aria-hidden="true">
                                <circle cx="7.5" cy="7.5" r="2.2" />
                                <path d="M7.5 1.6 V3.2 M7.5 11.8 V13.4 M1.6 7.5 H3.2 M11.8 7.5 H13.4 M3.4 3.4 L4.5 4.5 M10.5 10.5 L11.6 11.6 M11.6 3.4 L10.5 4.5 M4.5 10.5 L3.4 11.6" />
                            </svg>
                            Settings
                        </button>
                        {active && (
                            <button type="button" className="ls-btn" disabled={busy !== null}
                                onClick={() => window.confirm('Stop this pass? What it captured stays; the next Start begins from the first ZIP again.')
                                    && act('stop', trueOwnersApi.stop, 'Stopped. Start begins a new pass from the first ZIP.')}>
                                {busy === 'stop' ? 'Stopping…' : 'Stop'}
                            </button>
                        )}
                        {active && settings.enabled && (
                            <button type="button" className="ls-btn ls-btn--primary" disabled={busy !== null}
                                onClick={() => act('pause', trueOwnersApi.pause, 'Pausing — the parcels in hand finish first.')}>
                                {busy === 'pause' ? 'Pausing…' : 'Pause'}
                            </button>
                        )}
                        {active && !settings.enabled && (
                            <button type="button" className="ls-btn ls-btn--primary" disabled={busy !== null}
                                onClick={() => act('resume', trueOwnersApi.resume, 'Resumed — carrying on from the same parcels.')}>
                                {busy === 'resume' ? 'Resuming…' : 'Resume'}
                            </button>
                        )}
                        {!active && (
                            <button type="button" className="ls-btn ls-btn--primary" disabled={busy !== null}
                                onClick={() => act('start', trueOwnersApi.start, 'Started — walking every ZIP from the first.')}>
                                {busy === 'start' ? 'Starting…' : 'Start'}
                            </button>
                        )}
                    </div>
                </div>

                {notice && (
                    <div className={'ls-alert ls-alert--' + notice.kind}>
                        <span className="ls-alert__text">{notice.text}</span>
                        <button type="button" className="ls-alert__dismiss" aria-label="Dismiss" onClick={() => setNotice(null)}>
                            ×
                        </button>
                    </div>
                )}

                <div className={'ls-card ls-hero' + (run ? '' : ' ls-hero--empty')}>
                    <div className="ls-hero__head">
                        <div className="ls-hero__headings">
                            <div className="ls-hero__kicker">{run ? (active ? 'Current pass' : 'Last pass') : 'No pass yet'}</div>
                            {run && (
                                <div className="ls-hero__meta">
                                    <Pill state={shownState} />
                                    <span className="ls-hero__time">
                                        started {formatRunTime(run.started_at)} · {run.started_by}
                                        {run.finished_at ? ' · ended ' + formatClock(run.finished_at) : ''}
                                    </span>
                                </div>
                            )}
                        </div>
                        {run && (
                            <button type="button" className="ls-btn ls-btn--quiet" onClick={() => setOpenRun(run)}>
                                {active ? 'Watch parcels' : 'Open pass'}
                            </button>
                        )}
                    </div>

                    {!run && (
                        <div className="ls-hero__head">
                            <p className="ls-hero__empty">
                                Nothing has been captured yet. Start walks every ZIP in the client's territories,
                                asks LightBox for the true owners of every parcel, and files them as investors.
                            </p>
                        </div>
                    )}

                    {active && !settings.enabled && (
                        <div className={'ls-alert ' + (stoppedForKey ? 'ls-alert--error' : 'ls-alert--warn') + ' ls-hero__error'}>
                            <span className="ls-alert__mark">!</span>
                            <span className="ls-alert__text">
                                {stoppedForKey && lastNote ? lastNote.text : (PAUSE_REASON[status.paused_reason] || 'Paused.')}
                            </span>
                            {stoppedForKey && (
                                <button type="button" className="ls-alert__retry" onClick={() => setSettingsOpen(true)}>
                                    Enter a new key
                                </button>
                            )}
                        </div>
                    )}

                    {active && settings.enabled && state === 'budget_exhausted' && (
                        <div className="ls-alert ls-alert--warn ls-hero__error">
                            <span className="ls-alert__mark">!</span>
                            <span className="ls-alert__text">
                                Today's {formatNumber(usage.budget)} requests are spent. It carries on tomorrow, or sooner if the cap is raised.
                            </span>
                        </div>
                    )}

                    {run && (
                        <div className="ls-progress">
                            <div className="ls-progress__head">
                                <span className="ls-progress__read">{formatNumber(progress.zips_done)}</span>
                                <span className="ls-progress__scale">of {formatNumber(progress.zips_total)} ZIPs done</span>
                                <span className="ls-progress__percent">{percent}%</span>
                                {active && walking.length > 0 && (
                                    <span className="ls-progress__eta">
                                        now in {walking.map((lane) => lane.zip).join(', ')}
                                    </span>
                                )}
                            </div>
                            <div className="ls-progress__pages">
                                <div className="ls-progress__page">
                                    <div className={'ls-progress__fill' + (percent === 100 ? ' is-full' : '')} style={{ width: percent + '%' }} />
                                </div>
                            </div>
                            <div className="ls-progress__foot">
                                <span className="ls-progress__pagelabel">
                                    {formatNumber(progress.parcels_read)} parcels read this pass
                                    · {formatNumber(usage.today)} requests today{usage.budget > 0 ? ' of ' + formatNumber(usage.budget) : ''}
                                    · {formatNumber(status.people)} people on file
                                </span>
                                {running && <span className="ls-progress__live" />}
                                {running && <span className="ls-progress__fresh">refreshed {age}s ago</span>}
                            </div>
                        </div>
                    )}

                    {run && <Counts run={run} />}

                    {!stoppedForKey && notes.slice(-3).map((note, index) => (
                        <div key={index} className="ls-hero__offband"><span>{note.text}</span></div>
                    ))}

                    {run && run.error && (
                        stopped ? (
                            <div className="ls-hero__offband"><span>{run.error}</span></div>
                        ) : (
                            <div className="ls-alert ls-alert--error ls-hero__error">
                                <span className="ls-alert__mark">!</span>
                                <span className="ls-alert__text">{run.error}</span>
                            </div>
                        )
                    )}
                </div>

                <div className="ls-sectionhead">
                    <h2 className="ls-sectionhead__title">Passes</h2>
                </div>

                {runs.length === 0 ? (
                    <div className="ls-card ls-empty">No passes recorded yet.</div>
                ) : (
                    <div className="ls-card">
                        <div className="ls-table__scroll">
                            <div className="ls-table ls-table--runs">
                                <div className="ls-table__head">
                                    <div>Pass</div>
                                    <div>Status</div>
                                    {COUNTS.map(([key, label]) => <div key={key} className="is-numeric">{label}</div>)}
                                    <div>Started by</div>
                                </div>

                                {runs.map((entry) => (
                                    <div
                                        key={entry.id}
                                        className={'ls-table__row' + (entry.status === 'running' ? ' is-running' : '')}
                                        onClick={() => setOpenRun(entry)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyPress={(event) => event.key === 'Enter' && setOpenRun(entry)}
                                    >
                                        <div>
                                            <div className="ls-table__primary">{formatRunTime(entry.started_at)}</div>
                                            <div className="ls-mono ls-table__sub">pass #{entry.id}</div>
                                        </div>
                                        <div><Pill state={entry.id === (run && run.id) ? shownState : passState(entry)} cell /></div>
                                        {COUNTS.map(([key]) => (
                                            <div key={key} className={['ls-mono is-numeric', entry[key] ? '' : 'is-zero', key === 'failed' && entry[key] ? 'is-bad' : ''].filter(Boolean).join(' ')}>
                                                {formatNumber(entry[key])}
                                            </div>
                                        ))}
                                        <div className="ls-table__by">{entry.started_by}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ls-pager">
                            <span className="ls-pager__label">{formatRange(runsMeta, runs.length)}</span>
                            <button type="button" className="ls-btn ls-btn--small" disabled={page <= 1} onClick={() => loadRuns(page - 1)}>
                                Newer
                            </button>
                            <button type="button" className="ls-btn ls-btn--small"
                                disabled={!runsMeta || page >= runsMeta.last_page} onClick={() => loadRuns(page + 1)}>
                                Older
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {settingsOpen && (
                <SettingsDialog settings={settings} contactsKey={status.contacts_key} onSave={saveSettings} onClose={closeSettings} />
            )}
        </div>
    );
}
